import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppWindow, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useSession } from "@/hooks/useSession";
import { api, type Application } from "@/lib/api";
import { fmtAgo } from "@/lib/utils";

const schema = z.object({
  name: z.string().min(1).max(120),
  server_names: z.string().min(1, "at least one hostname"),
  profile: z.enum(["browser_site", "json_api", "webhook_receiver", "websocket_app", "large_uploads"]),
  origin_scheme: z.enum(["http", "https"]),
  origin_host: z.string().min(1),
  origin_port: z.coerce.number().int().min(1).max(65535),
  detect_only: z.boolean().default(true),
});
type Form = z.infer<typeof schema>;

const STATUS_VARIANT: Record<string, "success" | "warning" | "secondary" | "destructive"> = {
  active: "success", detect: "warning", draft: "secondary", error: "destructive",
};

export default function Applications() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canEdit = user?.role === "owner" || user?.role === "operator";
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const { data } = useQuery({
    queryKey: ["apps"],
    queryFn: () => api.get<Application[]>("/api/applications"),
    refetchInterval: 20_000,
  });
  const create = useMutation({
    mutationFn: (f: Form) =>
      api.post<Application>("/api/applications", {
        name: f.name,
        server_names: f.server_names.split(/[\s,]+/).filter(Boolean),
        profile: f.profile,
        detect_only: f.detect_only,
        cert_method: "none",
        variables: {},
        origin: {
          scheme: f.origin_scheme, host: f.origin_host, port: f.origin_port,
          tls_verify: true, sni: "",
        },
      }),
    onSuccess: () => {
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (e) => setErr(e.message),
  });

  const { register, handleSubmit, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { profile: "browser_site", origin_scheme: "http", origin_port: 8080, detect_only: true },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Applications"
        description="Services protected by the BunkerWeb edge — onboard in detection-only, review telemetry, then enforce."
      >
        {canEdit && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" /> Onboard application
          </Button>
        )}
      </PageHeader>

      <Dialog open={showForm} onClose={() => setShowForm(false)} wide
        title="Onboard an application"
        description="Creates a draft service in detection-only mode — nothing blocks until you review telemetry and apply.">
        <form onSubmit={handleSubmit((f) => create.mutate(f))} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <Input {...register("name")} placeholder="Customer portal" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Hostnames (space/comma separated)</Label>
            <Input {...register("server_names")} placeholder="app.example.com www.example.com" />
            {errors.server_names && <p className="text-xs text-destructive">{errors.server_names.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Profile</Label>
            <Select {...register("profile")}>
              <option value="browser_site">Browser site</option>
              <option value="json_api">JSON API</option>
              <option value="webhook_receiver">Webhook receiver</option>
              <option value="websocket_app">WebSocket app</option>
              <option value="large_uploads">Large uploads</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Origin</Label>
            <div className="flex gap-2">
              <Select {...register("origin_scheme")} className="w-24">
                <option value="http">http</option>
                <option value="https">https</option>
              </Select>
              <Input {...register("origin_host")} placeholder="10.0.0.5 or app.internal" className="flex-1" />
              <Input {...register("origin_port")} type="number" className="w-24" placeholder="8080" />
            </div>
            <p className="text-xs text-muted-foreground">
              Loopback/link-local/metadata origins are rejected by the API.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" {...register("detect_only")} defaultChecked />
            Detection-only (log would-be blocks, enforce nothing) — recommended start
          </label>
          {err && <p className="text-sm text-destructive sm:col-span-2">{err}</p>}
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button disabled={create.isPending}>{create.isPending ? "Creating…" : "Create draft"}</Button>
          </div>
        </form>
      </Dialog>

      <Card>
        <Table>
          <THead>
            <TR>
              <TH>Name</TH><TH>Hostnames</TH><TH>Profile</TH><TH>Mode</TH>
              <TH>Status</TH><TH>Origin</TH><TH>Created</TH>
            </TR>
          </THead>
          <TBody>
            {(data ?? []).map((a) => (
              <TR key={a.id}>
                <TD>
                  <Link to={`/applications/${a.id}`} className="font-medium text-primary hover:underline">
                    {a.name}
                  </Link>
                </TD>
                <TD className="font-mono text-xs">{a.server_names.join(" ")}</TD>
                <TD className="text-xs">{a.profile}</TD>
                <TD><Badge variant={a.detect_only ? "warning" : "default"}>{a.detect_only ? "detect" : "block"}</Badge></TD>
                <TD>
                  <Badge variant={STATUS_VARIANT[a.status] ?? "secondary"}>{a.status}</Badge>
                  {a.protection === "reduced" && (
                    <Badge variant="destructive" className="ml-1" title={Object.entries(a.disabled_controls ?? {}).map(([k, v]) => `${k}: ${v}`).join("; ") || "controls disabled"}>
                      reduced
                    </Badge>
                  )}
                  {a.nx && a.nx.state !== "none" && (
                    <Badge variant={a.nx.state === "enforcing" ? "default" : "warning"} className="ml-1"
                      title={`NX desired: ${a.nx.desired.join(",") || "none"} — effective: ${a.nx.effective.join(",") || "none"}${a.nx.missing_plugins.length ? " — missing plugins: " + a.nx.missing_plugins.join(",") : ""}`}>
                      NX:{a.nx.state}
                    </Badge>
                  )}
                </TD>
                <TD className="font-mono text-xs">{a.origin_scheme}://{a.origin_host}:{a.origin_port}</TD>
                <TD className="text-xs text-muted-foreground">{fmtAgo(a.created_at)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {(data ?? []).length === 0 && (
          <EmptyState icon={AppWindow} title="No applications yet"
            hint="Onboard one to start protecting it — starts in detection-only, nothing blocks until you apply." />
        )}
      </Card>
    </div>
  );
}
